import { useState, useRef, useEffect } from 'react';
import { Search, ChevronDown, MapPin, Menu, X, Phone, MessageCircle } from 'lucide-react';
import { communes } from '@/mocks/communes';

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [communeOpen, setCommuneOpen] = useState(false);
  const [communeSearch, setCommuneSearch] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const communeRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (communeRef.current && !communeRef.current.contains(e.target as Node)) {
        setCommuneOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setCommuneOpen(false);
  }, [location.pathname]);

  const filteredCommunes = communes.filter((c) =>
    c.name.toLowerCase().includes(communeSearch.toLowerCase()),
  );

  const navLinks = [
    { to: '/', label: 'Accueil' },
    { to: '/nos-prestations/', label: 'Prestations' },
    { to: '/motorisation-volet-charleroi/', label: 'Motorisation' },
    { to: '/zones-dinterventions/', label: 'Zones' },
    { to: '/contact/', label: 'Contact' },
  ];

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header
      className={`sticky top-0 z-40 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-sm border-b border-background-200' : 'bg-white'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center shrink-0"
          >
            <img src="/logo.svg" alt="Volet Charleroi — Réparation de volets roulants" className="h-9 md:h-11 w-auto" width="250" height="48" />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-0.5 xl:gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-2 py-2 xl:px-3 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                  isActive(link.to)
                    ? 'text-accent-500 bg-accent-50'
                    : 'text-foreground-600 hover:text-primary-500 hover:bg-background-100'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Desktop actions */}
          <div className="hidden lg:flex items-center gap-1 xl:gap-1.5 2xl:gap-2">
            {/* Commune selector */}
            <div className="relative" ref={communeRef}>
              <button
                onClick={() => setCommuneOpen(!communeOpen)}
                className="flex items-center gap-1.5 px-2 py-2 xl:px-2.5 2xl:px-3 text-sm font-medium text-foreground-600 hover:text-primary-500 hover:bg-background-100 rounded-md transition-colors whitespace-nowrap cursor-pointer"
              >
                <MapPin className="w-4 h-4" />
                <span className="hidden lg:inline 2xl:hidden">Commune</span>
                <span className="hidden 2xl:inline">Ma commune</span>
                <ChevronDown className={`w-3 h-3 transition-transform ${communeOpen ? 'rotate-180' : ''}`} />
              </button>

              {communeOpen && (
                <div className="absolute top-full right-0 mt-1 w-64 bg-white rounded-xl shadow-lg border border-background-200 p-3 z-50">
                  <div className="relative mb-2">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground-400" />
                    <input
                      type="text"
                      placeholder="Rechercher une commune..."
                      value={communeSearch}
                      onChange={(e) => setCommuneSearch(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-accent-400 text-sm"
                    />
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {filteredCommunes.map((commune) => (
                      <Link
                        key={commune.slug}
                        to={commune.urlPath}
                        className="block px-3 py-2 text-sm text-foreground-600 hover:bg-accent-50 hover:text-accent-500 rounded-md transition-colors whitespace-nowrap"
                        onClick={() => setCommuneOpen(false)}
                      >
                        {commune.name}
                      </Link>
                    ))}
                    {filteredCommunes.length === 0 && (
                      <p className="px-3 py-2 text-sm text-foreground-400">Aucune commune trouvée</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Phone */}
            <a
              href="tel:+3280020410"
              className="flex items-center gap-1.5 px-2 py-2 xl:px-2.5 2xl:px-3 text-sm font-semibold text-primary-500 hover:text-accent-500 transition-colors whitespace-nowrap"
            >
              <Phone className="w-4 h-4" />
              <span className="hidden xl:inline">0800 20 410</span>
            </a>

            {/* WhatsApp - visible only at 2xl */}
            <a
              href="https://wa.me/32475585219"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden 2xl:flex items-center gap-1.5 px-2 py-2 2xl:px-3 text-sm font-medium text-success-500 hover:text-success-600 transition-colors whitespace-nowrap"
            >
              <MessageCircle className="w-4 h-4" />
              <span>WhatsApp</span>
            </a>

            {/* CTA Devis */}
            <Link
              to="/contact/"
              className="btn-primary text-sm px-4 py-2.5 2xl:px-5 whitespace-nowrap"
            >
              Devis
            </Link>
          </div>

          {/* Mobile buttons */}
          <div className="flex lg:hidden items-center gap-2">
            <a
              href="tel:+3280020410"
              className="p-2 text-primary-500 hover:text-accent-500 transition-colors"
              aria-label="Appeler"
            >
              <Phone className="w-5 h-5" />
            </a>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer"
              aria-label="Menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-background-200 py-4 px-2 animate-in">
            {/* Commune selector mobile */}
            <div className="mb-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground-400" />
                <input
                  type="text"
                  placeholder="Rechercher votre commune..."
                  value={communeSearch}
                  onChange={(e) => setCommuneSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2.5 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-accent-400"
                />
              </div>
              {communeSearch && (
                <div className="mt-2 max-h-36 overflow-y-auto border border-background-200 rounded-lg">
                  {filteredCommunes.map((commune) => (
                    <Link
                      key={commune.slug}
                      to={commune.urlPath}
                      className="block px-3 py-2 text-sm text-foreground-600 hover:bg-accent-50 hover:text-accent-500 transition-colors"
                      onClick={() => {
                        setMobileMenuOpen(false);
                        setCommuneSearch('');
                      }}
                    >
                      <MapPin className="w-3.5 h-3.5 inline mr-1.5" />
                      {commune.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Nav links mobile */}
            <nav className="flex flex-col gap-1 mb-3">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`px-3 py-2.5 text-sm font-medium rounded-md transition-colors ${
                    isActive(link.to)
                      ? 'text-accent-500 bg-accent-50'
                      : 'text-foreground-600 hover:text-primary-500 hover:bg-background-100'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Action buttons mobile */}
            <div className="flex flex-col gap-2">
              <a
                href="https://wa.me/32475585219"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-success-500 hover:bg-success-600 rounded-md transition-colors whitespace-nowrap"
              >
                <MessageCircle className="w-4 h-4" />
                WhatsApp
              </a>
              <Link
                to="/contact/"
                className="btn-primary text-sm py-2.5 text-center whitespace-nowrap"
              >
                Devis gratuit
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}